


import { useState } from "react";
import support from "../../Configurations/addressConfig.json";


const FloatingSupportWidget = ({ phone = support.call }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <>
      <style>{WIDGET_CSS}</style>

      {/* ── Circular Headset Trigger ── */}
      {!open && (
        <button
          className="fsw-orb"
          onClick={() => setOpen(true)}
          aria-label="Call Support"
        >
          {/* Spinning rainbow border ring */}
          <span className="fsw-orb-ring" aria-hidden="true" />
          {/* Inner circle with headset SVG */}
          <span className="fsw-orb-inner">
            <HeadsetIcon />
          </span>
          {/* Pulse ripple */}
          <span className="fsw-orb-pulse" aria-hidden="true" />
        </button>
      )}

      {/* ── Expanded card ── */}
      {open && (
        <div className="fsw-card">
          <button
            className="fsw-close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            ✕
          </button>

          <div className="fsw-header">
            <span className="fsw-header-icon">🩺</span>
            <div>
              <p className="fsw-header-title">Worried About Your Health?</p>
              <p className="fsw-header-sub">Talk to an Expert Now!</p>
            </div>
          </div>

          <div className="fsw-divider" />

          <a href={`tel:${phone}`} className="fsw-call-btn">
            📞 &nbsp;Call: {formatPhone(phone)}
          </a>

          <p className="fsw-trust">Free · Confidential · Available 24/7</p>

          <button className="fsw-dismiss" onClick={() => setDismissed(true)}>
            Maybe later
          </button>
        </div>
      )}
    </>
  );
};

/* ── Neon Outline Headset SVG Icon ───────────────────────────────────────── */
const HeadsetIcon = () => (
  <svg viewBox="0 0 48 48" width="42" height="42" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="fsw-glow" x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="1.8" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    {/* Glow layer */}
    <g filter="url(#fsw-glow)" opacity="0.75">
      <path d="M8 26C8 15.5 15.5 8 24 8C32.5 8 40 15.5 40 26"
        fill="none" stroke="#00e5ff" strokeWidth="3.5" strokeLinecap="round" />
      <rect x="4" y="24" width="8" height="12" rx="4"
        fill="none" stroke="#00e5ff" strokeWidth="3" />
      <rect x="36" y="24" width="8" height="12" rx="4"
        fill="none" stroke="#00e5ff" strokeWidth="3" />
      <path d="M44 34 Q44 43 36 43 L30 43"
        fill="none" stroke="#00e5ff" strokeWidth="3" strokeLinecap="round" />
      <circle cx="28" cy="43" r="3" fill="#00e5ff" />
    </g>
    {/* Sharp crisp lines on top */}
    <path d="M8 26C8 15.5 15.5 8 24 8C32.5 8 40 15.5 40 26"
      fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    <rect x="4" y="24" width="8" height="12" rx="4"
      fill="rgba(0,229,255,0.15)" stroke="#ffffff" strokeWidth="1.5" />
    <rect x="36" y="24" width="8" height="12" rx="4"
      fill="rgba(0,229,255,0.15)" stroke="#ffffff" strokeWidth="1.5" />
    <path d="M44 34 Q44 43 36 43 L30 43"
      fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    <circle cx="28" cy="43" r="2" fill="white" />
  </svg>
);

/* ── Helper ───────────────────────────────────────────────────────────────── */
const formatPhone = (p) => {
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
};

/* ── CSS ──────────────────────────────────────────────────────────────────── */
const WIDGET_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;600&display=swap');

  /* ─── Orb container ─── */
  .fsw-orb {
    position: fixed;
    right: 20px;
    bottom: 208px;
    width: 88px;
    height: 88px;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    z-index: 8888;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ─── Revolving rainbow border ring ─── */
  .fsw-orb-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    /* conic-gradient spins around the circle */
    background: conic-gradient(
      from 0deg,
      #00e5ff,
      #00bcd4,
      #00897b,
      #006D77,
      #26c6da,
      #80deea,
      #00e5ff
    );
    animation: fsw-spin 2.4s linear infinite;
    /* mask: leave only a 3px ring visible */
    -webkit-mask: radial-gradient(
      farthest-side,
      transparent calc(100% - 3.5px),
      #000 calc(100% - 3.5px)
    );
    mask: radial-gradient(
      farthest-side,
      transparent calc(100% - 3.5px),
      #000 calc(100% - 3.5px)
    );
  }

  @keyframes fsw-spin {
    to { transform: rotate(360deg); }
  }

  /* ─── Inner filled circle ─── */
  .fsw-orb-inner {
    position: absolute;
    inset: 4px;           /* gap between ring and fill */
    border-radius: 50%;
    background: linear-gradient(135deg, #001a1f 0%, #002a30 55%, #003d45 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 20px rgba(0, 109, 119, 0.55), 0 0 18px rgba(0,229,255,0.15) inset;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .fsw-orb:hover .fsw-orb-inner {
    transform: scale(1.08);
    box-shadow: 0 10px 28px rgba(0, 109, 119, 0.7);
  }

  /* ─── Pulse ripple ─── */
  .fsw-orb-pulse {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(0, 180, 200, 0.5);
    animation: fsw-pulse 2s ease-out infinite;
    pointer-events: none;
  }

  @keyframes fsw-pulse {
    0%   { transform: scale(1);   opacity: 0.7; }
    70%  { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }

  /* ─── Card ─── */
  .fsw-card {
    position: fixed;
    right: 16px;
    bottom: 128px;
    width: 260px;
    background: #FFFFFF;
    border-radius: 20px;
    box-shadow: 0 16px 48px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,109,119,0.12);
    z-index: 8999;
    overflow: hidden;
    animation: fsw-slide-in 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards;
    font-family: 'DM Sans', sans-serif;
  }

  @keyframes fsw-slide-in {
    from { opacity: 0; transform: translateY(20px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }

  .fsw-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(255,255,255,0.25);
    border: none;
    width: 28px; height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    display: flex; align-items: center; justify-content: center;
    z-index: 2;
    transition: background 0.2s;
  }
  .fsw-close:hover { background: rgba(255,255,255,0.4); }

  .fsw-header {
    background: linear-gradient(135deg, #003D45 0%, #006D77 55%, #008B98 100%);
    padding: 18px 16px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
  }
  .fsw-header-icon {
    font-size: 30px;
    flex-shrink: 0;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  }
  .fsw-header-title {
    font-family: 'Sora', sans-serif;
    font-size: 14px;
    font-weight: 800;
    color: #FFFFFF;
    margin: 0 0 3px;
    line-height: 1.3;
  }
  .fsw-header-sub {
    font-size: 11px;
    color: rgba(255,255,255,0.72);
    margin: 0;
  }

  .fsw-divider { height: 1px; background: #E2E8F0; }

  .fsw-call-btn {
    display: block;
    margin: 16px 16px 0;
    padding: 13px;
    background: linear-gradient(135deg, #006D77, #008B98);
    color: #FFFFFF;
    font-family: 'Sora', sans-serif;
    font-size: 15px;
    font-weight: 800;
    border-radius: 13px;
    text-align: center;
    text-decoration: none;
    letter-spacing: 0.3px;
    box-shadow: 0 4px 14px rgba(0,109,119,0.38);
    transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
  }
  .fsw-call-btn:hover {
    background: linear-gradient(135deg, #005F68, #007A85);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0,109,119,0.48);
  }

  .fsw-trust {
    font-size: 11px;
    color: #94A3B8;
    text-align: center;
    margin: 10px 0 0;
    padding: 0 16px;
    letter-spacing: 0.2px;
  }

  .fsw-dismiss {
    display: block;
    width: 100%;
    padding: 10px;
    margin-top: 6px;
    background: none;
    border: none;
    font-size: 12px;
    font-weight: 600;
    color: #CBD5E1;
    cursor: pointer;
    text-align: center;
    transition: color 0.2s;
    margin-bottom: 4px;
  }
  .fsw-dismiss:hover { color: #94A3B8; }

  /* ─── Mobile ─── */
  @media (max-width: 640px) {
    .fsw-orb {
      right: 16px;
      bottom: 20px;
      width: 76px;
      height: 76px;
    }
    .fsw-card {
      right: 12px;
      bottom: 116px;
      width: calc(100vw - 24px);
      max-width: 320px;
    }
  }
`;

export default FloatingSupportWidget;